import { IDonorStatus, IDonorRegistration, IListItem } from '@nkr/models/index';
import { IDashRemoteStatus } from '@nkr/models/donor-status-by-center';
import { IDashWorkFlowStatusRequest } from '@nkr/models/dash-workflow-status-request';
import { ICoordinator } from '../coordinator';
import { DashRegistrationStep } from '@nkr/enums/index';
import { Alert, Col, Panel, Row } from 'react-bootstrap';
import DonorLog from './donor-log';
import ActionsDropdown from './actions-dropdown';
import DashStatusForm from './status-form';

interface IProps {
    centerId: number,
    data: IDonorRegistration,
    statusList: IDonorStatus[],
    remoteStatusList: IDashRemoteStatus[],
    actionList: IListItem[],
    coList: ICoordinator[],
    ruleOutReasons: Array<{ id: number; description: string }>,
    statusFormBaselineKey?: number,
    onStatusChange: (request: IDashWorkFlowStatusRequest) => void,
    onSaveCenterCard?: () => void | Promise<void>,
    onSaveNetworkCard?: () => void | Promise<void>,
    onDataChange: (field: string, value: any) => void,
    onSaveLogEntry: (dashRegistrationId: number, actionTaken: number, comments: string) => void,
    onUpdateLogEntry: (id: number, dashRegistrationId: number, actionTaken: number, comments: string) => void,
    onDeleteLogEntry: (id: number, dashRegistrationId: number) => void,
    onItemSelected: (id: number, action: number) => void,
    onRuleOut: (donorId: number, reasonId: number, allowReRegistration: boolean, useNkrAssistance: boolean, coordinatorName?: string, phoneNumber?: string, medicalReasonCategory?: number, medicalReasonNotes?: string) => void
}

const ViewDonorRegistrationData = (props: IProps) => {
    if (props.data == null) {
        return null;
    }

    const data = props.data;
    const disableAllFields = props.data.currentRegistrationStep == DashRegistrationStep.Transferred || props.data.isReadOnly;

    return (
        <div>
            <Panel>
                <Panel.Body>
                    {props.data.currentRegistrationStep == DashRegistrationStep.Transferred && (
                        <Alert bsStyle='warning' bsSize='sm'>This donor was transferred; this record is here for visibility only...</Alert>
                    )}
                    {props.data.isReadOnly && (
                        <Alert bsStyle='warning' bsSize='sm'>This donor is visible through your DASH domain. Workflow updates and donor edits are read-only.</Alert>
                    )}

                    <Alert bsStyle='info' style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ width: '70%' }}>
                                <Row>
                                    <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Source:</Col>
                                    <Col xs={12} lg={6}>{data.source}</Col>
                                </Row>

                                <Row>
                                    <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Name:</Col>
                                    <Col xs={12} lg={6}>{data.fullName}</Col>
                                </Row>

                                <Row>
                                    <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Email:</Col>
                                    <Col xs={12} lg={6}>{data.email}</Col>
                                </Row>

                                <Row>
                                    <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Phone Number:</Col>
                                    <Col xs={12} lg={6}>{data.phoneNumber}</Col>
                                </Row>
                                <Row>
                                    <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Location:</Col>
                                    <Col xs={12} lg={6}>{data.location}</Col>
                                </Row>
                                <Row>
                                    <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Miles from Center:</Col>
                                    <Col xs={12} lg={6}>{data.milesFromCenter ?? '--'}</Col>
                                </Row>
                                <Row>
                                    <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Requested Donor Mentor:</Col>
                                    <Col xs={12} lg={6}>{data.donorConnectRequested ? 'Yes' : 'No'}</Col>
                                </Row>
                                <Row>
                                    <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Mentor Assigned:</Col>
                                    <Col xs={12} lg={6}>{data.connectToDonorAssignedFirstName} {data.connectToDonorAssignedLastName}</Col>
                                </Row>
                            </div>
                            <div>
                                {props.data.currentRegistrationStep != DashRegistrationStep.Transferred && (
                                    <ActionsDropdown
                                        donor={data}
                                        showLinkToDetails={true}
                                        showBsMenu={true}
                                        onMenuToggle={() => null}
                                        onItemSelected={props.onItemSelected}
                                        isSuperAdmin={false}
                                    />
                                )}
                            </div>
                        </div>
                    </Alert>

                    {!disableAllFields &&
                        <div>
                            <DashStatusForm
                                key={`status-form-${data.id}-${props.statusFormBaselineKey || 0}`}
                                data={data}
                                statusList={props.statusList}
                                remoteStatusList={props.remoteStatusList}
                                coList={props.coList}
                                useWideFormat={false}
                                onDataChange={props.onDataChange}
                                onUpdate={props.onStatusChange}
                                onSaveCenterCard={props.onSaveCenterCard}
                                onSaveNetworkCard={props.onSaveNetworkCard}
                                ruleOutReasons={props.ruleOutReasons}
                                onRuleOut={props.onRuleOut}
                                isReadOnly={disableAllFields}
                            />
                        </div>
                    }

                    <DonorLog
                        data={data}
                        statusList={props.statusList}
                        actionList={props.actionList}
                        isMobile={false}
                        disabled={disableAllFields}
                        onSaveLogEntry={props.onSaveLogEntry}
                        onUpdateLogEntry={props.onUpdateLogEntry}
                        onDeleteLogEntry={props.onDeleteLogEntry}
                    />
                </Panel.Body>
            </Panel>
        </div>
    );
}

export default ViewDonorRegistrationData;
